import { useCallback, useMemo } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import * as state from '@/components/Analysis/state';
import {
  FileAttributes,
  useUpdatePointOfInterestFileMutation,
  FileUploadStatus,
  useUploadAttachmentsToPoiMutation,
  AllPointsOfInterestForInspectionDataLoaderDocument,
  SelectedPointOfInterestDocument,
  ImageType,
} from '@/__generated__/graphql';
import { Alert } from '@mui/material';
import {
  getSizeFromBase64,
  uploadBase64ToUrl,
} from '../PointOfInterestTab/SelectedPoiContent/Tabs/UploadFileToUrl';
import { ScreenshotLocationType } from '@/types';
import { canvasToImage } from '@/utils/dom';
import {
  BLISTER,
  BLISTER_TYPE,
  DEFAULT_SCREENSHOT_DARK_BACKGROUND,
  DEFAULT_SCREENSHOT_LIGHT_BACKGROUND,
  PIT_TYPE,
} from '@/constants';
import { useCaptureScreenshot } from '@/hooks/useCaptureScreenshot';
import { usePoiLabelMarkerColors } from '@/hooks/usePoiLabelMarkerColors';
import { blisterPolygons, editPointOfInterest as editCurrentPoi, selectedPointOfInterest, selectedTemplate } from '@/components/Analysis/modules/pointOfInterest/state';
import { convertTemplateNameToPoiType } from '@/utils';

export type SceneOptions = {
  showPointCloudInSpherical?: boolean;
  arePoisVisible?: boolean;
  areSphericalsVisible?: boolean;
  showPointOfInterestPointer?: boolean;
};

type SaveScreenshotProps = {
  location?: ScreenshotLocationType;
  poiId: string | undefined;
  setIsUpdating?: React.Dispatch<React.SetStateAction<boolean>>;
  isUpdating?: boolean;
  refetchPoi?: boolean;
  showSnackbar?: boolean;
  showPoiMask?: boolean
};

type ImageTypeProps = {
  imageType: ImageType;
};

type ViewPointProps = {
  background?:
  | typeof DEFAULT_SCREENSHOT_DARK_BACKGROUND
  | typeof DEFAULT_SCREENSHOT_LIGHT_BACKGROUND;
};

export const useSavePoiScreenshot = ({ imageType }: ImageTypeProps) => {
  const selectedSpherical = useAtom(state.selectedSpherical);
  const callbacks = useAtomValue(state.globalCallbacks);
  const setSnackbarMessage = useSetAtom(state.snackbarMessage);
  const [uploadAttachmentsToPoi] = useUploadAttachmentsToPoiMutation();
  const [updatePoiFile] = useUpdatePointOfInterestFileMutation();
  const setAreSphericalsVisible = useSetAtom(state.areSphericalsVisible);
  const setShowPointCloudInSpherical = useSetAtom(state.showPointCloudInSpherical);
  const setArePoisVisible = useSetAtom(state.arePoisVisible);
  const setPolygons = useSetAtom(blisterPolygons);
  const currentPoi = useAtomValue(selectedPointOfInterest)
  const editPointOfInterest = useAtomValue(editCurrentPoi);
  const selectedPoiTemplate = useAtomValue(selectedTemplate);
  const isMaskPoi = selectedPoiTemplate
    ? selectedPoiTemplate.name === BLISTER
    : currentPoi?.blister ?? false;
  const name = editPointOfInterest?.formState ? editPointOfInterest?.template?.name : currentPoi?.name ?? undefined;
  const poiType = convertTemplateNameToPoiType(name);
  const currentSpherical = useAtomValue(state.viewerCurrentSpherical);
  const [showPointOfInterestPointer, setShowPointOfInterestPointer] = useAtom(
    state.showPointOfInterestPointer
  );
  const poiName = useMemo(() => {
    return poiType === BLISTER_TYPE ? BLISTER_TYPE : poiType === PIT_TYPE ? PIT_TYPE : currentPoi?.type
  }, [currentPoi, poiType])
  const { captureElement } = useCaptureScreenshot();
  const { setPdfPoiLabelMarkerColors, resetPoiLabelMarkerColors } = usePoiLabelMarkerColors();
  const getViewpointScreenshot = useCallback(
    ({ background = DEFAULT_SCREENSHOT_DARK_BACKGROUND }: ViewPointProps) => {
      const container = document.getElementById('outer-viewer');

      if (!container) {
        const abyssViewerParent = document.querySelector('canvas');

        if (!abyssViewerParent) {
          const htmlCanvas = callbacks?.getHTMLCanvas && callbacks.getHTMLCanvas();
          return Promise.resolve(canvasToImage(htmlCanvas, { fillStyle: background }));
        }

        return captureElement({ element: abyssViewerParent, background });
      }

      return captureElement({ element: container, background });
    },
    [callbacks, captureElement]
  );

  // Usage example:
  //    prevOptions = setSceneOptions(myOptions);
  //    setTimeout(()=>{
  //          handleSavePoiScreenshot();
  //          setSceneOptions(prevOptions);   }, 500);
  const setSceneOptions = useCallback(
    (options: SceneOptions): SceneOptions => {
      const prevOptions: SceneOptions = {};

      const { showPointCloudInSpherical, arePoisVisible, areSphericalsVisible } = options;

      if (showPointCloudInSpherical !== undefined) {
        setShowPointCloudInSpherical((current) => {
          prevOptions['showPointCloudInSpherical'] = current;
          return showPointCloudInSpherical;
        });
      }
      if (arePoisVisible !== undefined) {
        setArePoisVisible((current) => {
          prevOptions['arePoisVisible'] = current;
          return arePoisVisible;
        });
      }
      if (areSphericalsVisible !== undefined) {
        setAreSphericalsVisible((current) => {
          prevOptions['areSphericalsVisible'] = current;
          return areSphericalsVisible;
        });
      }
      if (showPointOfInterestPointer !== undefined) {
        setShowPointOfInterestPointer((current) => {
          prevOptions['showPointOfInterestPointer'] = current;
          return showPointOfInterestPointer;
        });
      }
      return prevOptions;
    },
    [
      showPointOfInterestPointer,
      setShowPointCloudInSpherical,
      setArePoisVisible,
      setAreSphericalsVisible,
      setShowPointOfInterestPointer,
    ]
  );

  const handleSavePoiScreenshot = useCallback(
    async ({ location = 'screenshots', poiId, isUpdating, setIsUpdating, showPoiMask }: SaveScreenshotProps) => {
      const pointOfInterestScreenShot: FileAttributes[] = []
      if (isUpdating) {
        return setSnackbarMessage({
          shouldShow: true,
          content: (
            <Alert severity="warning">
              Unable to take Screenshot at the moment. Other files are uploading. Please try once
              uploading is done
            </Alert>
          ),
        });
      } else {
        if (setIsUpdating) setIsUpdating(true);
        const viewpointScreenshot = await getViewpointScreenshot({
          background: DEFAULT_SCREENSHOT_DARK_BACKGROUND,
        });

        // Set PDF annotation colors before taking PDF screenshot
        setPdfPoiLabelMarkerColors();
        let screenshotForPdf;
        try {
          screenshotForPdf = await getViewpointScreenshot({
            background: DEFAULT_SCREENSHOT_LIGHT_BACKGROUND,
          });
        } finally {
          // Reset annotation colors after PDF screenshot (even if it fails)
          resetPoiLabelMarkerColors();
        }
        setShowPointOfInterestPointer(false);
        if (!viewpointScreenshot || !poiId || !screenshotForPdf) {
          if (setIsUpdating) setIsUpdating(false);
          return '';
        }

        // Always save to files instead of screenshots
        // using same uuid for both files so that while filtering files we can check if white background image exists for that particular image or not
        const uuid = `file-${Date.now()}`;
        const screenshotFile: FileAttributes = {
          uuid,
          name: `POI_SCREENSHOT_${Date.now()}`,
          mimeType: 'image/jpeg',
          size: getSizeFromBase64(viewpointScreenshot),
          imageType: imageType,
        };

        const screenshotFileForPdf: FileAttributes = {
          uuid: `${uuid}-${ImageType.WhiteBgPdf}`,
          name: `POI_SCREENSHOT_PDF_${Date.now()}`,
          mimeType: 'image/jpeg',
          size: getSizeFromBase64(screenshotForPdf),
          imageType: ImageType.WhiteBgPdf,
        };

        pointOfInterestScreenShot.push(screenshotFile, screenshotFileForPdf)
        let poiMaskScreenShot: string | undefined
        if (showPoiMask && isMaskPoi) {
          const sceneOptions: SceneOptions = {
            areSphericalsVisible: false,
            ...(currentSpherical
              ? {
                showPointCloudInSpherical: false,
                arePoisVisible: false,
              }
              : {}),
          };
          const prevSceneOptions = setSceneOptions(sceneOptions);

          setPolygons((prev) => {
            if (!prev) return prev;
            const polygon = prev.find((polygon) => polygon.id?.startsWith(poiId));
            if (!polygon) return prev;
            polygon.isVisible = true;
            return [...prev];
          });

          await new Promise((resolve) => setTimeout(resolve, 500));
          poiMaskScreenShot = await getViewpointScreenshot({
            background: DEFAULT_SCREENSHOT_DARK_BACKGROUND,
          });
          await new Promise((resolve) => setTimeout(resolve, 500));
          setPolygons((prev) => {
            if (!prev) return prev;
            const polygon = prev.find((polygon) => polygon.id?.startsWith(poiId));
            if (!polygon) return prev;
            polygon.isVisible = false;
            return [...prev];
          });
          setSceneOptions(prevSceneOptions);
          if (!poiMaskScreenShot) return;
          const poiProfileMaskFile = {
            uuid,
            name: `${poiName}_MASK_VIEW_${Date.now()}`,
            mimeType: 'image/jpeg',
            size: getSizeFromBase64(poiMaskScreenShot),
            imageType: ImageType.Other,
          };
          pointOfInterestScreenShot.push(poiProfileMaskFile);
        }

        const mutationVariables = {
          input: {
            pointOfInterestId: poiId,
            files: [...pointOfInterestScreenShot],
            lastModifiedFor: 'Capture View',
          },
        };

        const { data } = await uploadAttachmentsToPoi({
          variables: mutationVariables,
          refetchQueries: [AllPointsOfInterestForInspectionDataLoaderDocument],
          onError: (error) => {
            setSnackbarMessage({
              shouldShow: true,
              content: (
                <Alert severity="error" variant="filled">
                  {error.message}
                </Alert>
              ),
            });
          },
        });

        if (data?.uploadAttachmentsToPoi && data?.uploadAttachmentsToPoi?.length > 0) {
          const file = data.uploadAttachmentsToPoi[0];
          const fileForPdf = data.uploadAttachmentsToPoi[1];
          const poiProfileMaskScreenshot = data.uploadAttachmentsToPoi[2];
          if (poiMaskScreenShot) {
            const uploadPoiMaskFile = await uploadBase64ToUrl(poiMaskScreenShot, poiProfileMaskScreenshot.url);
            if (uploadPoiMaskFile) {
              updatePoiFile({
                variables: {
                  input: {
                    pointOfInterestId: poiId,
                    fileId: poiProfileMaskScreenshot.id,
                    uploadStatus: FileUploadStatus.Success,
                  },
                },
              });
            }
          }
          const uploadedFile = await uploadBase64ToUrl(viewpointScreenshot, file.url);
          const uploadedFileForPdf = await uploadBase64ToUrl(screenshotForPdf, fileForPdf.url);
          if (uploadedFile && uploadedFileForPdf) {
            updatePoiFile({
              variables: {
                input: {
                  pointOfInterestId: poiId,
                  fileId: fileForPdf.id,
                  uploadStatus: FileUploadStatus.Success,
                },
              },
            });
            updatePoiFile({
              variables: {
                input: {
                  pointOfInterestId: poiId,
                  fileId: file.id,
                  uploadStatus: FileUploadStatus.Success,
                },
              },
              refetchQueries: [SelectedPointOfInterestDocument],
              onCompleted: () => {
                // Only show success message for manual screenshots, not auto snapshots
                if (location !== 'attachments') {
                  setSnackbarMessage({
                    shouldShow: true,
                    content: (
                      <Alert severity="success" variant="filled">
                        Screenshot has been successfully uploaded
                      </Alert>
                    ),
                  });
                }
              },
              onError: (error) => {
                if (setIsUpdating) setIsUpdating(false);
                setSnackbarMessage({
                  shouldShow: true,
                  content: (
                    <Alert severity="error" variant="filled">
                      {error.message}
                    </Alert>
                  ),
                });
              },
            });
          }
          if (setIsUpdating) setIsUpdating(false);
        }
      }
    },
    [
      setSnackbarMessage,
      getViewpointScreenshot,
      setPdfPoiLabelMarkerColors,
      setShowPointOfInterestPointer,
      imageType,
      uploadAttachmentsToPoi,
      resetPoiLabelMarkerColors,
      updatePoiFile,
      selectedSpherical,
      currentSpherical,
      setSceneOptions,
      setPolygons,
      poiName,
      isMaskPoi,
    ]
  );

  return { handleSavePoiScreenshot, setSceneOptions };
};
